import React, { useEffect, useRef, useState } from 'react';
import ConsentManager from '../../lib/consentManager';
import consentConfig from '../../config/consentConfig';
import '../../styles/modals.css';
import './CookiePreferencesModal.css';

const FocusTrap = ({ active, children, onDeactivate }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!active) return;
    const focusableSelectors = [
      'a[href]',
      'button',
      'input',
      'select',
      'textarea',
      '[tabindex]:not([tabindex="-1"])',
    ];

    const container = containerRef.current;
    if (!container) return;

    const focusable = Array.from(
      container.querySelectorAll(focusableSelectors.join(','))
    ).filter(el => !el.hasAttribute('disabled'));

    // Focus first focusable
    focusable[0]?.focus();

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onDeactivate?.();
      }
      if (e.key !== 'Tab') return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    container.addEventListener('keydown', handleKeyDown);
    return () => container.removeEventListener('keydown', handleKeyDown);
  }, [active, onDeactivate]);

  return <div ref={containerRef}>{children}</div>;
};

const Toggle = ({ id, label, description, locked, checked, onChange }) => {
  return (
    <div className="category">
      <div>
        <div id={`${id}-label`} style={{ fontWeight: 700 }}>
          {label}
          {locked && (
            <span aria-hidden="true" style={{ marginLeft: 6, fontSize: 12 }}>
              (always on)
            </span>
          )}
        </div>
        {description && (
          <div id={`${id}-desc`} style={{ fontSize: 13, color: '#4a4a4a' }}>
            {description}
          </div>
        )}
      </div>
      <button
        role="switch"
        aria-checked={!!checked}
        aria-labelledby={`${id}-label`}
        aria-describedby={description ? `${id}-desc` : undefined}
        className="switch"
        onClick={() => !locked && onChange?.(!checked)}
        disabled={locked}
      >
        <span className="dot" />

      </button>
    </div>
  );
};

const CookiePreferencesModal = ({ open, onClose }) => {
  const [state, setState] = useState(() => {
    return (
      ConsentManager.getConsent() || ConsentManager.defaultConsent()
    );
  });

  useEffect(() => {
    if (!open) return;
    // Sync with cookie each open
    const current = ConsentManager.getConsent() || ConsentManager.defaultConsent();
    setState(current);
  }, [open]);

  if (!open) return null;

  const updateCategory = (key, value) => {
    setState((s) => ({
      ...s,
      categories: { ...s.categories, [key]: value },
    }));
  };

  const save = async () => {
    const next = ConsentManager.setConsent((c) => ({ ...c, categories: state.categories }));
    // Optional server sync for authenticated users is handled app-side via API if needed
    onClose?.(next);
  };

  const copy = consentConfig.copy?.modal;

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="cookie-prefs-title">
      <div className="modal" aria-describedby="cookie-prefs-desc">
        <FocusTrap active onDeactivate={onClose}>
          <header>
            <h2 id="cookie-prefs-title">{copy?.title || 'Cookie preferences'}</h2>
            <button className="btn secondary" onClick={onClose} aria-label="Close preferences">{copy?.buttons?.close || 'Close preferences'}</button>
          </header>
          <p id="cookie-prefs-desc">
            {copy?.description || 'Manage your cookie preferences by category.'}
          </p>

          <Toggle
            id="cat-necessary"
            label={copy?.categories?.necessary?.label || 'Necessary'}
            description={copy?.categories?.necessary?.description || 'Required for core site functionality.'}
            locked
            checked
          />

          <Toggle
            id="cat-functional"
            label={copy?.categories?.functional?.label || 'Functional'}
            description={copy?.categories?.functional?.description || 'Enhance features like remembering preferences.'}
            checked={state.categories.functional}
            onChange={(v) => updateCategory('functional', v)}
          />

          <Toggle
            id="cat-analytics"
            label={copy?.categories?.analytics?.label || 'Analytics/Performance'}
            description={copy?.categories?.analytics?.description || 'Help us understand site usage to improve performance.'}
            checked={state.categories.analytics}
            onChange={(v) => updateCategory('analytics', v)}
          />

          <Toggle
            id="cat-advertising"
            label={copy?.categories?.advertising?.label || 'Advertising/Targeting'}
            description={copy?.categories?.advertising?.description || 'Used to deliver relevant advertising and measure effectiveness.'}
            checked={state.categories.advertising}
            onChange={(v) => updateCategory('advertising', v)}
          />

          <footer>
            <button className="btn secondary" onClick={onClose}>{copy?.buttons?.cancel || 'Cancel'}</button>
            <button className="btn" onClick={save}>{copy?.buttons?.save || 'Save preferences'}</button>
          </footer>
        </FocusTrap>
      </div>
    </div>
  );
};

export default CookiePreferencesModal;
